import React, { cloneElement, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomPopover } from '../../../components/custom-popover';
import getFormatMessage from '../../../locales/get-format-message';
import { IRootState } from '../../../store/reducers';
import { RbacRoleObjectKey, IUserPermissionInfo } from '../../../model';
import styles from './index.module.scss';
import { NoPermissionCard } from './no-permission';
import { fetchUserPermission } from '../../../store/modules/permission';
import { cloneDeep } from 'lodash';
import { ModalWrap } from '../../../components/modal-wrap';

interface IProps {
  readonly contentType?: 'page' | 'action';
  readonly forceShow?: boolean;
  readonly permissionKey?: RbacRoleObjectKey;
  readonly gameID?: string;
  readonly envID?: string;
  readonly serviceName?: string;
  readonly tipsPlacement?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';
  readonly children: React.ReactElement;
  readonly otherTips?: string;
  readonly anyPermission?: boolean; // 是否拥有至少一个权限
  readonly customCheck?: (perms: RbacRoleObjectKey[]) => boolean; // 自定义
  readonly preCheck?: () => {
    disabled: boolean;
    tips?: string;
  };
  readonly noPermContent?: ReactNode;
}

export const PermissionCheck = (props: IProps) => {
  const {
    permissionKey,
    gameID: propsGameID,
    envID: propsEnvID,
    contentType = 'action',
    forceShow,
    otherTips,
    anyPermission,
    noPermContent,
    customCheck,
    preCheck,
  } = props;

  const commonDispatch = useDispatch<any>();
  const savedPermission = useSelector((state: IRootState) => state.permission);
  const studioID = useSelector(
    (state: IRootState) => state.studio.selectStudio?.studioID
  );
  const storeGameID = useSelector(
    (state: IRootState) => state.games.selectGame?.gameID
  );
  const storeEnvID = useSelector(
    (state: IRootState) => state.games.selectEnv?.id
  );

  const queryGameID = propsGameID || storeGameID || '';
  const queryEnvID = propsEnvID || storeEnvID || '';

  useEffect(() => {
    queryPermission();
  }, [propsGameID, propsEnvID]);

  const queryPermission = () => {
    if (forceShow) {
      return;
    }
    if (propsEnvID && !savedPermission.envPerm[propsEnvID]) {
      fetchUserPermission(commonDispatch)({
        gameID: propsGameID,
        envID: propsEnvID ? Number(propsEnvID) : undefined,
      });
      return;
    }
    if (propsGameID && !savedPermission.gamePerm[propsGameID]) {
      fetchUserPermission(commonDispatch)({
        gameID: propsGameID,
      });
      return;
    }
  };

  const isFetched = () => {
    if (!studioID) {
      return false;
    }
    let fetched = !!savedPermission.studioPrem[studioID];
    if (queryGameID) {
      fetched =
        !!savedPermission.studioPrem[studioID] &&
        !!savedPermission.gamePerm[queryGameID];
    }

    if (queryEnvID) {
      fetched = fetched && !!savedPermission.envPerm[queryEnvID];
    }

    return fetched;
  };

  const hasPermission = (
    permissionInfo: IUserPermissionInfo = savedPermission
  ) => {
    let currentPerms = [...(permissionInfo.studioPrem[studioID || ''] || [])];

    if (propsGameID) {
      currentPerms = [
        ...(permissionInfo.gamePerm[propsGameID || storeGameID || ''] || []),
      ];
    }
    if (propsEnvID) {
      currentPerms = [
        ...(permissionInfo.envPerm[propsEnvID || storeEnvID || ''] || []),
      ];
    }
    if (customCheck) {
      return customCheck(currentPerms as RbacRoleObjectKey[]);
    }
    if (permissionKey) {
      return currentPerms.includes(permissionKey);
    }
    if (anyPermission && currentPerms.length > 0) {
      return true;
    }

    if (propsEnvID) {
      return !!permissionInfo.envPerm?.[propsEnvID]?.length;
    }

    if (propsGameID) {
      return !!permissionInfo.gamePerm?.[propsGameID]?.length;
    }

    return false;
  };

  if (!isFetched()) {
    if (forceShow) {
      return (
        <>
          {cloneElement(props.children, {
            className: `${props.children.props.className} ${styles.noPermissionItem}`,
            onClickCapture: async (ev: MouseEvent) => {
              ev.stopPropagation();

              const infos = await fetchUserPermission(commonDispatch)({
                gameID: propsGameID,
                envID: propsEnvID ? Number(propsEnvID) : undefined,
              });

              const newPermissionInfo =
                cloneDeep<IUserPermissionInfo>(savedPermission);

              if (propsGameID) {
                newPermissionInfo.gamePerm[propsGameID] = infos.gamePerm;
              }

              if (propsEnvID) {
                newPermissionInfo.envPerm[propsEnvID] = infos.envPerm;
              }

              if (!hasPermission(newPermissionInfo)) {
                ModalWrap.warn({
                  title: getFormatMessage('no_permission_title'),
                  children: (
                    <span>{getFormatMessage('no_rbac_permission_action')}</span>
                  ),
                  oKText: getFormatMessage('i_know'),
                  maskClosable: true,
                });
                return false;
              }
              props.children.props.onClick?.();
            },
          })}
        </>
      );
    }
    return <></>;
  }

  const preCheckRes = preCheck?.();

  if (preCheckRes?.disabled) {
    return (
      <CustomPopover
        content={preCheckRes.tips || ''}
        placement={props.tipsPlacement}
      >
        <>
          {cloneElement(props.children, {
            disabled: true,
            className: `${props.children.props.className} ${styles.noPermissionItem}`,
            onClickCapture: (ev: MouseEvent) => {
              ev.stopPropagation();
            },
          })}
        </>
      </CustomPopover>
    );
  }

  if (!hasPermission()) {
    if (contentType === 'action') {
      if (noPermContent !== undefined) {
        return <>{noPermContent}</>;
      }
      return (
        <CustomPopover
          content={getFormatMessage('no_rbac_permission_action')}
          placement={props.tipsPlacement}
        >
          <>
            {cloneElement(props.children, {
              disabled: true,
              className: `${props.children.props.className} ${styles.noPermissionItem}`,
              onClickCapture: (ev: MouseEvent) => {
                ev.stopPropagation();
              },
            })}
          </>
        </CustomPopover>
      );
    }
    if (contentType === 'page') {
      return <NoPermissionCard />;
    }
  }

  if (otherTips) {
    return (
      <CustomPopover content={otherTips} placement={props.tipsPlacement}>
        <> {props.children}</>
      </CustomPopover>
    );
  }

  return props.children;
};
